import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Compass, Ban, Users, Trees, Zap, ExternalLink } from "lucide-react";

const PRINCIPLES = [
  { icon: Shield, title: "Encode purpose into governance", body: "Write mission and founder-first principles into articles of incorporation, board duties and investor filters — so purpose is a legal obligation, not just aspiration." },
  { icon: Ban, title: "Define red lines and exit paths", body: "Decide up front which capital, partners and exits would compromise the mission — and enshrine those limits in your governance." },
  { icon: Users, title: "Appoint mission guardians", body: "Create explicit roles whose job is to defend purpose when pressure or lucrative offers arrive." },
  { icon: Compass, title: "Design against misuse & surrogation", body: "Rule out foreseeable misuses of the product. Beware of metrics becoming stand-ins for the mission itself." },
  { icon: Trees, title: "Structure for long-term flourishing", body: "Build governance that rewards long-term, mission-aligned outcomes over short-term extraction (Long-Term Stock Exchange, Spiritual Holding Company)." },
  { icon: Zap, title: "Non-negotiable founder-first values", body: "'Founder-first' and 'no extraction' aren't preferences — they are constraints that filter every decision." },
];

const QUESTIONS = [
  "If someone offered a lucrative partnership that diverged from our mission, would our legal and structural mechanisms let us say no?",
  "Are our founding principles in the articles of incorporation and shareholder agreements — or just the culture handbook?",
  "When short-term financial pressure hits, do our governance structures empower us to resist 'financial gravity'?",
  "Do our hiring, compensation and promotion practices reward mission-alignment — or short-term wins?",
  "Can we clearly list which decisions, partners or funding sources would fundamentally compromise our mission?",
];

const EXAMPLES = [
  { title: "Costco's $1.50 hot dog", body: "Costco rebuilt its supply chain rather than raise the price. Costly, visible commitment to a founder-first principle." },
  { title: "Patagonia's purpose trust", body: "'Earth is now our only shareholder.' A Spiritual Holding Company that prioritises human flourishing over financial return." },
  { title: "FedMart's golden goose", body: "A mission-led success destroyed once ownership stopped protecting its ethos. Mission alone fails if the surrounding system favours extraction." },
  { title: "Mondragon's constellation", body: "An ecosystem of organisations tightly linked by shared mission — each with its own mission-lock, in bidirectional relationship with the core." },
];

export default function MissionLock() {
  return (
    <div className="space-y-10" data-testid="mission-lock-page">
      <Link to="/resources" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#25AAAA] hover:underline">
        <ArrowLeft size={12}/> Back to resources
      </Link>

      <section className="relative border-2 border-[#0F1F2E] bg-[#0F1F2E] text-white p-8 md:p-12 overflow-hidden">
        <div className="absolute -right-24 -bottom-24 w-[320px] h-[320px] rounded-full border border-[#F4B847]/25 pointer-events-none" />
        <div className="absolute -right-12 -bottom-12 w-[200px] h-[200px] rounded-full border border-[#25AAAA]/40 pointer-events-none" />
        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#F4B847]">A lens for your value proposition</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3 leading-[0.95]">
          Mission Lock<br/>
          <span className="text-[#25AAAA]">& the Incorruptible.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-neutral-200">
          Distilled from Eric Ries&apos;s <em>Incorruptible</em>. Companies rarely fail because founders stopped caring — they
          drift because the structure quietly rewarded drift. <strong>Mission Lock</strong> is the deliberate design of governance,
          ownership and culture so that purpose becomes structurally protected, not just declared.
        </p>
        <p className="mt-3 max-w-3xl text-sm text-[#F4B847]">
          Use this lens on your sponsor&apos;s challenge. Where might financial gravity pull the solution off-mission over time?
          What structural moves would keep it honest?
        </p>
      </section>

      <section data-testid="mission-lock-principles">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-3">Six mechanisms for mission lock</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-[#E5E7EB]">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="border-r border-b border-[#E5E7EB] bg-white p-6">
                <div className="w-10 h-10 bg-[#0F1F2E] text-[#F4B847] flex items-center justify-center"><Icon size={18}/></div>
                <div className="font-display text-xl font-black tracking-tight text-[#0F1F2E] mt-4">{i+1}. {p.title}</div>
                <p className="text-sm text-[#525252] mt-2 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-2 border-[#25AAAA] bg-[#E6F7F7] p-6 md:p-8" data-testid="mission-lock-questions">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#1e7d7d]">Ask your team</div>
        <h2 className="font-display text-3xl font-black tracking-tight text-[#0F1F2E] mt-2">Five questions to test your mission lock</h2>
        <ol className="mt-5 space-y-3 text-sm md:text-base text-[#0F1F2E] leading-relaxed">
          {QUESTIONS.map((q, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 flex-shrink-0 bg-[#25AAAA] text-white font-bold flex items-center justify-center text-xs">{i+1}</span>
              <span>{q}</span>
            </li>
          ))}
        </ol>
      </section>

      <section data-testid="mission-lock-examples">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-3">Real-world moves</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-[#E5E7EB]">
          {EXAMPLES.map((e, i) => (
            <div key={i} className="border-r border-b border-[#E5E7EB] bg-white p-6">
              <div className="font-display text-lg font-bold tracking-tight text-[#25AAAA]">{e.title}</div>
              <p className="text-sm text-[#525252] mt-2 leading-relaxed">{e.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-[#0F1F2E] bg-white p-6 md:p-8" data-testid="mission-lock-application">
        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#F4B847]">Apply this to your sponsor</div>
        <h2 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E] mt-2">In your Value Proposition Canvas</h2>
        <p className="text-sm text-[#525252] mt-3 leading-relaxed max-w-3xl">
          In the <strong>Gain Creators</strong> and <strong>Products &amp; Services</strong> blocks, ask: does what we&apos;re proposing
          strengthen or weaken the sponsor&apos;s mission lock? Could our solution be structured (through ownership, governance, or a
          steward-ownership partner) so that it&apos;s incorruptible by future pressure?
        </p>
        <p className="text-sm text-[#525252] mt-3">
          A sustainable solution isn&apos;t just kinder — it&apos;s <strong>structurally harder to corrupt</strong>. That&apos;s the pitch to your sponsor.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/canvas/mission-lock" className="inline-flex items-center gap-2 bg-[#25AAAA] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#1e7d7d]" data-testid="mission-lock-worksheet-link">
            Open your Mission Lock Worksheet
          </Link>
          <Link to="/canvas/vpc" className="inline-flex items-center gap-2 border border-[#0F1F2E] text-[#0F1F2E] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0F1F2E] hover:text-white">
            Open your Value Proposition Canvas
          </Link>
          <a href="https://incorruptible.co" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#0F1F2E] text-[#0F1F2E] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0F1F2E] hover:text-white">
            More on Incorruptible <ExternalLink size={12}/>
          </a>
        </div>
      </section>
    </div>
  );
}
